import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { produce } from "immer";
import { changeLanguage, getDetectedLanguage, SUPPORTED_LANGUAGE_CODES } from "@/i18n";
import { appCodeEnum, appStore } from "@/app/store/appStore";
import { FxIdWebFeaturesAuthTwitchRefreshAccessTokenRequest, FxIdWebFeaturesProfileGetResponse } from "@/api/gen";
import OpenApiClient from "@/api/OpenApiClient";
import { emailStepStore } from "@entities/StepEmail/store/emailStepStore";
import { authTokenExists } from "@/api/Auth";

export interface IOtherLogin {
	username: string;
	refreshToken: string;
	lastActive: Date;
	appCode: appCodeEnum;
}

export interface ITransferRefused {
	login: string;
	till: number;
}

function removeDuplicatesLoginsKeepNewest(arr: IOtherLogin[]): IOtherLogin[] {
	const map = new Map<string, IOtherLogin>();

	arr.forEach((item) => {
		const existing = map.get(item.username);
		if (!existing || item.lastActive > existing.lastActive) {
			map.set(item.username, item);
		}
	});

	return Array.from(map.values()).sort((a, b) => {
		const dateA = new Date(a.lastActive);
		const dateB = new Date(b.lastActive);
		return dateB.getTime() - dateA.getTime();
	});
}

interface OauthState {
	location: string;
}
interface IPersistStore {
	clientGuid: string;
	otherLogins: IOtherLogin[];
	setOtherLogins: (value: IOtherLogin[]) => void;
	installPath: string | null;
	refreshToken: { [K in string]: string };
	setRefreshToken: (value: string | null) => void;
	installPathByApp: { [K in string]: string };
	installPathByAppAndBranch: { [K in string]: { [Key in string]: string } };
	setInstallPath: (path: string | null) => void;
	useCache: boolean;
	setUseCache: (value: boolean) => void;
	branch: string | null;
	setBranch: (value: string | null) => void;
	isPrivacy: boolean;
	setIsPrivacy: (value: boolean) => void;
	firstStartDate: string | null;
	lastReviewDate: string | null;
	setFirstStartDate: (date: string) => void;
	setLastReviewDate: (date: string) => void;

	//Модальное окно с настройками

	isDiagnosticStart: boolean;

	isAllResourcesUse: boolean;
	setAllResourcesUse: (value: boolean) => void;
	isAutoUpdate: boolean;
	setAutoUpdate: (value: boolean) => void;
	isPreviewUpdates: boolean;
	setPreviewUpdates: (value: boolean) => void;
	useP2P: boolean;
	setUseP2P: (value: boolean) => void;

	downloadSpeed: number;
	setDownloadSpeed: (value: number) => void;
	uploadSpeed: number;
	setUploadSpeed: (value: number) => void;
	p2pSpeed: number;
	setP2PSpeed: (value: number) => void;

	loading: boolean;
	error: string | null;
	language: string;
	setLanguage: (value: string | undefined) => void;
	lastNewsHashes: string[];
	setLastNewsHashes: (value: string[]) => void;
	preferredServer: string;
	setPreferredServer: (value: string) => void;
	a: number;
	b: number;
	setA: (value: number) => void;
	setB: (value: number) => void;
	buildNumberLoadingStarted: number;
	setBuildNumberLoadingStarted: (value: number) => void;
	profile: FxIdWebFeaturesProfileGetResponse | null;
	loadProfile: () => Promise<FxIdWebFeaturesProfileGetResponse | null>;
	updateProfile: (Language: string | null, MarketingEnabled: boolean | null) => void;
	marketingEnabled: boolean;
	setMarketingEnabled: (value: boolean) => void;
	twitchToken: string | null;
	transferRefusedList: Record<string, ITransferRefused>;
	setTransferRefused: (value: ITransferRefused) => void;
	stateAfterOauth: OauthState | null;

	setStateAfterOauth(param: OauthState): void;
}

export const persistStore = create<IPersistStore>()(
	persist(
		(set) => ({
			stateAfterOauth: null,
			setStateAfterOauth(param: OauthState) {
				set(
					produce((store: IPersistStore) => {
						store.stateAfterOauth = param;
					})
				);
			},
			firstStartDate: null,
			lastReviewDate: null,
			setFirstStartDate: (date: string) => set({ firstStartDate: date }),
			setLastReviewDate: (date: string) => set({ lastReviewDate: date }),
			clientGuid: uuidv4(),
			twitchToken: null,
			transferRefusedList: {},
			setTransferRefused: (value: ITransferRefused) => {
				set(
					produce((store: IPersistStore) => {
						store.transferRefusedList[value.login] = value;
					})
				);
			},
			otherLogins: [],
			setOtherLogins: (value: IOtherLogin[]) => {
				set(
					produce((store: IPersistStore) => {
						store.otherLogins = removeDuplicatesLoginsKeepNewest(value);
					})
				);
			},
			a: 0,
			b: 0,
			buildNumberLoadingStarted: 0,
			setBuildNumberLoadingStarted: (value: number) => {
				set(
					produce((store: IPersistStore) => {
						store.buildNumberLoadingStarted = value;
					})
				);
			},
			setA: (value: number) => {
				set(
					produce((store: IPersistStore) => {
						store.a = value;
					})
				);
			},
			setB: (value: number) => {
				set(
					produce((store: IPersistStore) => {
						store.b = value;
					})
				);
			},
			preferredServer: "",
			setPreferredServer: (value: string) => {
				set(
					produce((store: IPersistStore) => {
						store.preferredServer = value;
					})
				);
			},
			lastNewsHashes: [],
			setLastNewsHashes: (value: string[]) => {
				set(
					produce((store: IPersistStore) => {
						store.lastNewsHashes = value;
					})
				);
			},
			language: getDetectedLanguage(),
			setLanguage: (newLanguage: string | undefined) => {
				let value = newLanguage;
				if (!(value !== undefined && SUPPORTED_LANGUAGE_CODES.includes(value))) {
					value = SUPPORTED_LANGUAGE_CODES[0];
				}
				const supportedFromConfig = appStore.getState().gameConfig.SupportedLanguages;
				if (!supportedFromConfig.includes(value)) {
					value = supportedFromConfig[0];
				}

				const currentLanguage = persistStore.getState().language;
				if (currentLanguage === value) {
					console.log("Language has not changed. Skipping saving language change");
					return;
				}

				set(
					produce((store: IPersistStore) => {
						store.language = value;
					})
				);
				changeLanguage(value);
			},
			profile: null,
			loadProfile: async (): Promise<FxIdWebFeaturesProfileGetResponse | null> => {
				if (!authTokenExists()) {
					return null;
				}

				try {
					const res = await OpenApiClient.Profile.fxIdWebFeaturesProfileGetEndpoint({});

					let twitchToken = null;
					const appCode = appStore.getState().appCode;
					const TwitchGameData = res.Twitch?.TwitchGameData;
					if (TwitchGameData && TwitchGameData[appCode]) {
						twitchToken = TwitchGameData[appCode].AccessToken;
						const request: FxIdWebFeaturesAuthTwitchRefreshAccessTokenRequest = {
							Game: appCode
						};
						OpenApiClient.Auth.fxIdWebFeaturesAuthTwitchRefreshAccessTokenEndpoint(request).then((res) => {
							if (res.Data?.AccessToken) {
								set(
									produce((store: IPersistStore) => {
										store.twitchToken = res.Data?.AccessToken || "";
									})
								);
							}
						});
					}

					set(
						produce((store: IPersistStore) => {
							store.profile = res;
							store.twitchToken = twitchToken;
							store.marketingEnabled = res.MarketingEnabled;
						})
					);
					if (persistStore.getState().language !== res.Locale) {
						persistStore.getState().setLanguage(res.Locale);
					}

					return res;
				} catch (e) {
					console.error("Error fetching profile", e);
				}
				return null;
			},
			updateProfile: async (Language: string | null, MarketingEnabled: boolean | null) => {
				if (!authTokenExists()) {
					console.error("Cannot update profile without auth token");
					return;
				}
				try {
					const language = Language || persistStore.getState().language;
					const marketingEnabled =
						MarketingEnabled !== null
							? MarketingEnabled
							: persistStore.getState().profile?.MarketingEnabled;

					await OpenApiClient.Profile.fxIdWebFeaturesProfileUpdateProfileSettingsEndpoint({
						Language: language,
						MarketingEnabled: marketingEnabled,
						DUMMY: false
					});
					const value = await persistStore.getState().loadProfile();

					console.log("setProfile", Language, MarketingEnabled, value);
					if (value) {
						set(
							produce((store: IPersistStore) => {
								store.profile = value;
								store.marketingEnabled = value.MarketingEnabled;
								store.language = value.Locale;
							})
						);
					}
				} catch (e) {
					console.error("Error updating profile", e);
				}
			},
			marketingEnabled: false,
			setMarketingEnabled: (value: boolean) => {
				set(
					produce((store: IPersistStore) => {
						store.marketingEnabled = value;
					})
				);
			},
			isDiagnosticStart: true,

			isAllResourcesUse: false,
			isAutoUpdate: true,
			isPreviewUpdates: false,
			useP2P: true,

			downloadSpeed: -1,
			uploadSpeed: -1,
			p2pSpeed: 0,

			loading: false,
			error: null,

			installPath: null,
			refreshToken: {} as { [K in string]: string },
			setRefreshToken: (value) => {
				const { appCode } = appStore.getState();
				set(
					produce((store: IPersistStore) => {
						const { refreshToken } = store;
						const newRt = { ...refreshToken };
						newRt[appCode] = value || "";
						store.refreshToken = newRt;
					})
				);
			},
			isPrivacy: false,
			installPathByApp: {} as { [K in string]: string },
			installPathByAppAndBranch: {} as {
				[K in string]: { [Key in string]: string };
			},
			setIsPrivacy: (value) =>
				set(
					produce((store: IPersistStore) => {
						store.isPrivacy = value;
					})
				),

			setInstallPath: (path: string | null) => {
				const { appCode } = appStore.getState();
				const { branch } = persistStore.getState();
				if (!path) {
					set(
						produce((store: IPersistStore) => {
							store.installPath = null;
						})
					);
					return;
				}

				set(
					produce((store: IPersistStore) => {
						store.installPath = path;
						const { isExternalLogin } = emailStepStore.getState();
						if (!isExternalLogin) {
							const { installPathByApp, installPathByAppAndBranch } = store;

							installPathByApp[appCode] = path;

							if (branch !== null) {
								if (!installPathByAppAndBranch[appCode]) {
									installPathByAppAndBranch[appCode] = {};
								}
								installPathByAppAndBranch[appCode][branch] = path;
							}
						}
					})
				);
			},
			useCache: false,
			setUseCache: (value) =>
				set(
					produce((store: IPersistStore) => {
						store.useCache = value;
					})
				),
			branch: null,
			setBranch: (value) =>
				set(
					produce((store: IPersistStore) => {
						store.branch = value;
					})
				),
			setAllResourcesUse: (value: boolean) => {
				set(
					produce((store: IPersistStore) => {
						store.isAllResourcesUse = value;
					})
				);
			},
			setAutoUpdate: (value: boolean) => {
				set(
					produce((store: IPersistStore) => {
						store.isAutoUpdate = value;
					})
				);
			},
			setPreviewUpdates: (value: boolean) => {
				set(
					produce((store: IPersistStore) => {
						store.isPreviewUpdates = value;
					})
				);
			},
			setUseP2P: (value: boolean) => {
				set(
					produce((store: IPersistStore) => {
						store.useP2P = value;
					})
				);
			},

			setDownloadSpeed: (value: number) => {
				set(
					produce((store: IPersistStore) => {
						store.downloadSpeed = value;
					})
				);
			},
			setUploadSpeed: (value: number) => {
				set(
					produce((store: IPersistStore) => {
						store.uploadSpeed = value;
					})
				);
			},
			setP2PSpeed: (value: number) => {
				set(
					produce((store: IPersistStore) => {
						store.p2pSpeed = value;
					})
				);
			}
		}),

		{ name: "persistStore" }
	)
);
